<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
    message,
    Button,
    Card,
    Drawer,
    Modal,
    Spin,
    Select,
    Space
} from 'ant-design-vue';
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import { taipeiDay, taipeiToday } from '@/utils/taipeiTime';
import { getConsultationBookingStatusMeta } from '@/utils/consultationBookingStatus';
import {
    getConsultants,
    getConsultantsScheduleOverview,
    transferConsultation,
    getTransferRequests,
    acceptTransferRequest,
    rejectTransferRequest
} from '@/api/consultation/consultationApi';
import { usePermission } from '@/composables/usePermission';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    formatSlotTimeLabel,
    SLOT_STARTS
} from '@/constants/consultantScheduleSlots';
import { isWeekendBlackoutDay } from '@/utils/consultantScheduleMap';

dayjs.locale('zh-tw');

const router = useRouter();
const adminStore = useAdminInfo();
const { canView: canViewCalendar } = usePermission(
    'ConsultantScheduleCalendar'
);
const { canView: canManageConsultants } = usePermission('Consultants');
const canView = computed(
    () => canViewCalendar.value || canManageConsultants.value
);

const loading = ref(false);
const selectedMonth = ref(taipeiToday().slice(0, 7));
const filterConsultantId = ref('');
const viewMode = ref('open');
const consultants = ref([]);
const providers = ref([]);
const bookings = ref([]);
const levelByCmid = ref({});
const transferRequests = ref([]);
const transferActingId = ref(null);
const transferOpen = ref(false);
const transferBooking = ref(null);
const transferTarget = ref(undefined);
const transferSaving = ref(false);
const drawerOpen = ref(false);
const selectedDate = ref('');

const WEEKDAY_HEADERS = ['日', '一', '二', '三', '四', '五', '六'];
const CHIP_COLORS = [
    '#b45309',
    '#0f766e',
    '#1d4ed8',
    '#7c3aed',
    '#be123c',
    '#047857',
    '#c2410c',
    '#0369a1'
];

const monthLabel = computed(() =>
    dayjs(`${selectedMonth.value}-01`).format('YYYY年M月')
);

const monthRange = computed(() => {
    const start = dayjs(`${selectedMonth.value}-01`).startOf('month');
    return {
        from: start.format('YYYY-MM-DD'),
        to: start.endOf('month').format('YYYY-MM-DD')
    };
});

const isCurrentMonth = computed(
    () => selectedMonth.value === taipeiToday().slice(0, 7)
);

const nameByCmid = computed(() => {
    const map = {};
    for (const c of consultants.value) {
        if (c?.core_member_id) {
            map[c.core_member_id] = c.display_name || c.core_member_id;
        }
    }
    for (const p of providers.value) {
        const id = p?.core_member_id;
        if (!id || map[id]) continue;
        map[id] = p.name || id;
    }
    return map;
});

const consultantFilterOptions = computed(() => [
    { label: '全部諮詢師', value: '' },
    ...consultants.value
        .filter((c) => c.active !== false)
        .map((c) => ({
            label: c.display_name || c.core_member_id,
            value: c.core_member_id
        }))
]);

const displayName = (cmid) => nameByCmid.value[cmid] || cmid || '—';

const shortName = (name) => {
    const s = String(name || '');
    if (s.length <= 3) return s;
    return `${s.slice(0, 3)}…`;
};

const modeLabel = (mode) => (mode === 'offline' ? '現場' : mode === 'online' ? '線上' : '');

const bookingStatusLabel = (status) => getConsultationBookingStatusMeta(status).label;

const applyBooking = (person, match) => {
    person.student_name = match.student_name;
    person.mode = match.mode;
    person.status = match.status;
    person.consultation_id = match.id;
    person.required_level_name = match.required_level_name;
    person.required_level_rank = match.required_level_rank;
    person.starts_at = match.starts_at;
    person.booking = match;
};

const bookedCaption = (consultant, student) => {
    const who = String(consultant || '').trim() || '諮詢師';
    const studentName = String(student || '').trim();
    return studentName ? `${who}(${studentName})` : who;
};

const colorForCmid = (cmid) => {
    const s = String(cmid || '');
    let hash = 0;
    for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
    return CHIP_COLORS[hash % CHIP_COLORS.length];
};

const normalizeSlotStatus = (slot) => {
    const status = String(slot?.status ?? '').toLowerCase();
    if (status === 'booked' || Number(slot?.capacity_used) > 0) return 'booked';
    if (status === 'open') return 'open';
    return 'other';
};

/** date -> [{ core_member_id, name, open, booked, slots }] */
const dayConsultantMap = computed(() => {
    const byDate = new Map();
    const filterId = filterConsultantId.value;

    for (const provider of providers.value) {
        const cmid = provider?.core_member_id;
        if (!cmid) continue;
        if (filterId && cmid !== filterId) continue;

        const slots = Array.isArray(provider.slots) ? provider.slots : [];
        const perDay = new Map();

        for (const slot of slots) {
            const startsAt = slot?.starts_at ?? slot?.startsAt;
            if (!startsAt) continue;
            const d = taipeiDay(startsAt);
            if (!d.isValid()) continue;
            const date = d.format('YYYY-MM-DD');
            const kind = normalizeSlotStatus(slot);
            if (kind === 'other') continue;

            if (!perDay.has(date)) {
                perDay.set(date, {
                    core_member_id: cmid,
                    name: displayName(cmid),
                    color: colorForCmid(cmid),
                    open: 0,
                    booked: 0,
                    slots: []
                });
            }
            const row = perDay.get(date);
            if (kind === 'open') row.open += 1;
            else row.booked += 1;
            row.slots.push({
                startsAt,
                label: formatSlotTimeLabel(startsAt),
                kind
            });
        }

        for (const [date, row] of perDay.entries()) {
            row.slots.sort(
                (a, b) => taipeiDay(a.startsAt).valueOf() - taipeiDay(b.startsAt).valueOf()
            );
            if (!byDate.has(date)) byDate.set(date, []);
            byDate.get(date).push(row);
        }
    }

    for (const list of byDate.values()) {
        list.sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant'));
    }
    return byDate;
});

const bookingsByDate = computed(() => {
    const map = new Map();
    const filterId = filterConsultantId.value;
    for (const booking of bookings.value) {
        const cmid = booking?.consultant_core_member_id;
        if (!cmid) continue;
        if (filterId && cmid !== filterId) continue;
        const d = taipeiDay(booking.starts_at);
        if (!d.isValid()) continue;
        const date = d.format('YYYY-MM-DD');
        if (!map.has(date)) map.set(date, []);
        map.get(date).push({
            ...booking,
            hm: d.format('HH:mm'),
            consultant_name: displayName(cmid),
            color: colorForCmid(cmid)
        });
    }
    for (const list of map.values()) {
        list.sort((a, b) =>
            String(a.student_name || '').localeCompare(String(b.student_name || ''), 'zh-Hant')
        );
    }
    return map;
});

const heatClass = (open, booked) => {
    const total = open + booked;
    if (total <= 0) return 'heat-0';
    if (total <= 6) return 'heat-1';
    if (total <= 14) return 'heat-2';
    return 'heat-3';
};

const calendarCells = computed(() => {
    const start = dayjs(`${selectedMonth.value}-01`);
    const daysInMonth = start.daysInMonth();
    const leading = start.day();
    const cells = [];

    for (let i = 0; i < leading; i++) {
        cells.push({ empty: true, key: `pad-${i}` });
    }

    for (let d = 1; d <= daysInMonth; d++) {
        const day = start.date(d);
        const date = day.format('YYYY-MM-DD');
        const rows = dayConsultantMap.value.get(date) || [];
        const open = rows.reduce((s, r) => s + r.open, 0);
        const booked = rows.reduce((s, r) => s + r.booked, 0);
        const showingBooked = viewMode.value === 'booked';
        const previewSource = showingBooked
            ? bookingsByDate.value.get(date) || []
            : rows.filter((row) => row.open > 0);
        cells.push({
            empty: false,
            key: date,
            date,
            day: d,
            isToday: date === taipeiToday(),
            blocked: isWeekendBlackoutDay(day),
            consultants: rows,
            preview: previewSource.slice(0, 3),
            previewKind: showingBooked ? 'booked' : 'open',
            moreCount: Math.max(0, previewSource.length - 3),
            open,
            booked,
            hasSchedule: showingBooked ? previewSource.length > 0 : rows.length > 0,
            heat: heatClass(open, booked)
        });
    }

    return cells;
});

const selectedDayRows = computed(
    () => dayConsultantMap.value.get(selectedDate.value) || []
);

/** 時段 → 當日有開／已約的諮詢師（抽屜主視圖，避免每人重複列時段） */
const selectedDaySlots = computed(() => {
    const rows = selectedDayRows.value;
    const byStart = new Map();

    for (const row of rows) {
        for (const slot of row.slots || []) {
            const hm = taipeiDay(slot.startsAt).format('HH:mm');
            if (!byStart.has(hm)) {
                byStart.set(hm, {
                    start: hm,
                    label: slot.label,
                    open: [],
                    booked: []
                });
            }
            const bucket = byStart.get(hm);
            const person = {
                core_member_id: row.core_member_id,
                name: row.name,
                color: row.color
            };
            if (slot.kind === 'open') bucket.open.push(person);
            else if (slot.kind === 'booked') bucket.booked.push(person);
        }
    }

    const dayBookings = [...(bookingsByDate.value.get(selectedDate.value) || [])].sort((a, b) =>
        String(a.hm).localeCompare(String(b.hm))
    );
    const used = new Set();
    const orderedBuckets = [...byStart.values()].sort((a, b) => a.start.localeCompare(b.start));

    for (const bucket of orderedBuckets) {
        for (const person of bucket.booked) {
            const match = dayBookings.find(
                (item) =>
                    !used.has(item.id) &&
                    item.consultant_core_member_id === person.core_member_id &&
                    item.hm === bucket.start
            );
            if (!match) continue;
            used.add(match.id);
            applyBooking(person, match);
        }
    }

    for (const bucket of orderedBuckets) {
        for (const person of bucket.booked) {
            if (person.student_name) continue;
            const match = dayBookings.find(
                (item) =>
                    !used.has(item.id) &&
                    item.consultant_core_member_id === person.core_member_id
            );
            if (!match) continue;
            used.add(match.id);
            applyBooking(person, match);
        }
    }

    for (const item of dayBookings) {
        if (used.has(item.id)) continue;
        const hm = item.hm || '—';
        if (!byStart.has(hm)) {
            byStart.set(hm, {
                start: hm,
                label: formatSlotTimeLabel(item.starts_at),
                open: [],
                booked: []
            });
        }
        const person = {
            core_member_id: item.consultant_core_member_id,
            name: item.consultant_name,
            color: item.color
        };
        applyBooking(person, item);
        byStart.get(hm).booked.push(person);
        used.add(item.id);
    }

    const order = new Map(SLOT_STARTS.map((s, i) => [s, i]));
    return [...byStart.values()].sort((a, b) => {
        const ia = order.has(a.start) ? order.get(a.start) : 1000;
        const ib = order.has(b.start) ? order.get(b.start) : 1000;
        if (ia !== ib) return ia - ib;
        return a.start.localeCompare(b.start);
    });
});

const selectedDayTitle = computed(() => {
    if (!selectedDate.value) return '排班明細';
    return taipeiDay(selectedDate.value).format('M月D日（ddd）');
});

const monthSummary = computed(() => {
    let open = 0;
    let booked = 0;
    const people = new Set();
    for (const rows of dayConsultantMap.value.values()) {
        for (const r of rows) {
            open += r.open;
            booked += r.booked;
            people.add(r.core_member_id);
        }
    }
    return { open, booked, people: people.size };
});

const shiftMonth = (delta) => {
    selectedMonth.value = dayjs(`${selectedMonth.value}-01`)
        .add(delta, 'month')
        .format('YYYY-MM');
};

const goTodayMonth = () => {
    selectedMonth.value = taipeiToday().slice(0, 7);
};

const openDay = (cell) => {
    if (cell.empty || cell.blocked) return;
    selectedDate.value = cell.date;
    drawerOpen.value = true;
};

const canTransfer = (person) => {
    if (!person?.consultation_id) return false;
    if (canManageConsultants.value) return true;
    return (
        adminStore.core_member_id &&
        adminStore.core_member_id === person.core_member_id
    );
};

const transferOptions = computed(() => {
    const booking = transferBooking.value;
    if (!booking?.starts_at) return [];
    const date = taipeiDay(booking.starts_at).format('YYYY-MM-DD');
    const hm = taipeiDay(booking.starts_at).format('HH:mm');
    const openIds = new Set();
    for (const row of dayConsultantMap.value.get(date) || []) {
        if (
            row.slots?.some(
                (slot) => slot.kind === 'open' && taipeiDay(slot.startsAt).format('HH:mm') === hm
            )
        ) {
            openIds.add(row.core_member_id);
        }
    }
    return consultants.value
        .filter((consultant) => {
            const cmid = consultant.core_member_id;
            if (!cmid || cmid === booking.consultant_core_member_id) return false;
            if (!openIds.has(cmid)) return false;
            const rank = levelByCmid.value[cmid]?.level_rank;
            if (booking.required_level_rank == null || rank == null) return false;
            return rank >= booking.required_level_rank;
        })
        .map((consultant) => ({
            value: consultant.core_member_id,
            label: consultant.display_name || consultant.core_member_id
        }));
});

const openTransfer = (person) => {
    if (!person?.booking) return;
    transferBooking.value = person.booking;
    transferTarget.value = undefined;
    transferOpen.value = true;
};

const submitTransfer = async () => {
    if (!transferBooking.value?.id || !transferTarget.value) {
        message.warning('請選擇要接手的諮詢師');
        return Promise.reject(new Error('missing target'));
    }
    transferSaving.value = true;
    try {
        const res = await transferConsultation(transferBooking.value.id, {
            consultant_core_member_id: transferTarget.value
        });
        if (res.status !== 1) throw new Error(res.message || '轉移失敗');
        message.success(res.message || '已送出，待對方同意');
        transferOpen.value = false;
        await Promise.all([load(), loadTransferRequests()]);
    } catch (e) {
        message.error(e.message || '轉移失敗');
    } finally {
        transferSaving.value = false;
    }
};

const goConsultantSchedule = (cmid) => {
    if (!canManageConsultants.value) return;
    router.push({
        name: 'ConsultantSchedule',
        params: { coreMemberId: cmid },
        query: { month: selectedMonth.value }
    });
};

const loadTransferRequests = async () => {
    try {
        const res = await getTransferRequests();
        transferRequests.value = res.status === 1 && Array.isArray(res.data) ? res.data : [];
    } catch {
        transferRequests.value = [];
    }
};

const respondTransfer = async (row, accept) => {
    transferActingId.value = row.id;
    try {
        const res = accept
            ? await acceptTransferRequest(row.id)
            : await rejectTransferRequest(row.id);
        if (res.status !== 1) throw new Error(res.message || '處理失敗');
        message.success(accept ? '已同意接手' : '已拒絕');
        await Promise.all([load(), loadTransferRequests()]);
    } catch (e) {
        message.error(e.message || '處理失敗');
    } finally {
        transferActingId.value = null;
    }
};

const loadConsultants = async () => {
    const res = await getConsultants();
    if (res.status !== 1) throw new Error(res.message || '載入諮詢師失敗');
    consultants.value = Array.isArray(res.data) ? res.data : res.data?.items || [];
};

const loadOverview = async () => {
    const { from, to } = monthRange.value;
    const res = await getConsultantsScheduleOverview({ from, to });
    if (res.status !== 1) throw new Error(res.message || '載入排班失敗');
    const data = res.data;
    if (Array.isArray(data)) {
        providers.value = data;
        bookings.value = [];
        levelByCmid.value = {};
        return;
    }
    providers.value = Array.isArray(data?.providers) ? data.providers : [];
    bookings.value = Array.isArray(data?.bookings) ? data.bookings : [];
    const levels = {};
    for (const row of data?.consultants || []) {
        if (row?.core_member_id) levels[row.core_member_id] = row;
    }
    levelByCmid.value = levels;
};

const load = async () => {
    if (!canView.value) return;
    loading.value = true;
    try {
        await Promise.all([loadConsultants(), loadOverview()]);
    } catch (e) {
        message.error(e.message || '載入行事曆失敗');
        providers.value = [];
    } finally {
        loading.value = false;
    }
};

watch(selectedMonth, () => {
    void loadOverview().catch((e) => {
        message.error(e.message || '載入排班失敗');
        providers.value = [];
    });
});

onMounted(() => {
    adminStore.getAdminInfo();
    void load();
    void loadTransferRequests();
});
</script>

<template>
    <div class="sc pb-10">
        <p v-if="!canView" class="text-slate-500">無檢視權限</p>
        <template v-else>
            <Card
                :bordered="false"
                class="admin-panel mb-4 overflow-hidden rounded-2xl border border-[#ebe4d6] bg-white shadow-md"
            >
                <div class="sc__toolbar">
                    <div class="sc__nav">
                        <Button size="small" @click="shiftMonth(-1)">
                            <template #icon><LeftOutlined /></template>
                        </Button>
                        <span class="sc__month">{{ monthLabel }}</span>
                        <Button size="small" @click="shiftMonth(1)">
                            <template #icon><RightOutlined /></template>
                        </Button>
                        <Button
                            size="small"
                            :disabled="isCurrentMonth"
                            @click="goTodayMonth"
                        >
                            本月
                        </Button>
                    </div>
                    <Space wrap>
                        <Select
                            v-model:value="filterConsultantId"
                            :options="consultantFilterOptions"
                            show-search
                            :filter-option="
                                (input, option) =>
                                    (option?.label ?? '')
                                        .toLowerCase()
                                        .includes(String(input).toLowerCase())
                            "
                            class="sc__filter"
                            placeholder="篩選諮詢師"
                        />
                        <Button :loading="loading" @click="load">重新整理</Button>
                    </Space>
                </div>

                <div class="sc__meta">
                    <div class="sc__switch" role="tablist" aria-label="日曆顯示">
                        <button
                            type="button"
                            :class="{ 'is-on': viewMode === 'open' }"
                            @click="viewMode = 'open'"
                        >
                            可約 {{ monthSummary.open }}
                        </button>
                        <button
                            type="button"
                            :class="{ 'is-on': viewMode === 'booked' }"
                            @click="viewMode = 'booked'"
                        >
                            已約 {{ monthSummary.booked }}
                        </button>
                    </div>
                    <p class="sc__hint">
                        {{
                            viewMode === 'booked'
                                ? '格子裡是諮詢師（學員姓名）。點日期可把已約的人轉給同時段有空的諮詢師。'
                                : '格子裡是還有空檔的諮詢師。點日期可看當日時段。'
                        }}
                    </p>
                </div>
            </Card>

            <div v-if="transferRequests.length" class="sc__pending">
                <p class="sc__pending-title">轉移待同意</p>
                <div
                    v-for="row in transferRequests"
                    :key="row.id"
                    class="sc__pending-row"
                >
                    <span>
                        {{ row.from_name }} → {{ row.to_name }}
                        <template v-if="row.student_name">
                            · {{ row.student_name }}
                        </template>
                        · {{ row.starts_at ? taipeiDay(row.starts_at).format('M/D HH:mm') : '' }}
                        · {{ row.mode === 'offline' ? '現場' : '線上' }}
                    </span>
                    <span v-if="row.can_respond" class="sc__pending-actions">
                        <button
                            type="button"
                            class="sc__transfer-btn"
                            :disabled="transferActingId === row.id"
                            @click="respondTransfer(row, true)"
                        >
                            同意
                        </button>
                        <button
                            type="button"
                            class="sc__transfer-btn"
                            :disabled="transferActingId === row.id"
                            @click="respondTransfer(row, false)"
                        >
                            拒絕
                        </button>
                    </span>
                    <span v-else class="sc__pending-wait">等待對方</span>
                </div>
            </div>

            <Card
                :bordered="false"
                class="admin-panel overflow-hidden rounded-2xl border border-[#ebe4d6] bg-white shadow-md"
            >
                <div v-if="loading" class="sc__loading">
                    <Spin />
                    <span>載入排班中…</span>
                </div>
                <template v-else>
                    <div class="sc__weekdays" aria-hidden="true">
                        <span
                            v-for="(w, i) in WEEKDAY_HEADERS"
                            :key="w"
                            :class="{ 'is-weekend': i === 0 || i === 6 }"
                        >
                            {{ w }}
                        </span>
                    </div>
                    <div class="sc__grid" role="grid" aria-label="行事曆">
                        <button
                            v-for="cell in calendarCells"
                            :key="cell.key"
                            type="button"
                            class="sc__cell"
                            :class="[
                                cell.heat,
                                {
                                    'is-empty': cell.empty,
                                    'is-today': cell.isToday,
                                    'is-blocked': cell.blocked,
                                    'has-schedule': cell.hasSchedule
                                }
                            ]"
                            :disabled="cell.empty || cell.blocked"
                            @click="openDay(cell)"
                        >
                            <template v-if="!cell.empty">
                                <div class="sc__cell-top">
                                    <span class="sc__daynum">{{ cell.day }}</span>
                                    <span v-if="cell.hasSchedule" class="sc__pills">
                                        <span v-if="cell.open" class="sc__pill sc__pill--open">
                                            {{ cell.open }}
                                        </span>
                                        <span
                                            v-if="cell.booked"
                                            class="sc__pill sc__pill--booked"
                                        >
                                            {{ cell.booked }}
                                        </span>
                                    </span>
                                    <span v-else-if="cell.blocked" class="sc__blocked-tag">
                                        休
                                    </span>
                                </div>

                                <div v-if="cell.preview.length" class="sc__chips">
                                    <span
                                        v-for="row in cell.preview"
                                        :key="row.id || row.core_member_id"
                                        class="sc__chip"
                                        :class="{ 'sc__chip--student': cell.previewKind === 'booked' }"
                                        :title="
                                            cell.previewKind === 'booked'
                                                ? `${bookedCaption(row.consultant_name, row.student_name)} ${modeLabel(row.mode)}`
                                                : `${row.name}｜可約 ${row.open}`
                                        "
                                    >
                                        <i
                                            class="sc__chip-dot"
                                            :style="{ background: row.color }"
                                            aria-hidden="true"
                                        />
                                        <span v-if="cell.previewKind === 'booked'" class="sc__chip-booked">
                                            <span>{{ bookedCaption(row.consultant_name, row.student_name) }}</span>
                                            <span v-if="modeLabel(row.mode)" class="sc__chip-mode">{{
                                                modeLabel(row.mode)
                                            }}</span>
                                        </span>
                                        <template v-else>{{ shortName(row.name) }}</template>
                                    </span>
                                    <span v-if="cell.moreCount" class="sc__chip sc__chip--more">
                                        +{{ cell.moreCount }}
                                    </span>
                                </div>
                                <p v-else-if="!cell.blocked" class="sc__none">無排班</p>
                            </template>
                        </button>
                    </div>
                </template>
            </Card>

            <Drawer
                v-model:open="drawerOpen"
                :title="selectedDayTitle"
                placement="right"
                :width="420"
            >
                <p v-if="!selectedDaySlots.length" class="text-slate-500">
                    當日尚無諮詢師排班
                </p>
                <template v-else>
                    <div
                        v-for="slot in selectedDaySlots"
                        v-show="viewMode === 'open' ? slot.open.length : slot.booked.length"
                        :key="slot.start"
                        class="sc__slot"
                    >
                        <div class="sc__slot-head">
                            <strong class="sc__slot-time">{{ slot.label }}</strong>
                            <span class="sc__slot-meta">
                                <template v-if="slot.open.length">
                                    可約 {{ slot.open.length }}
                                </template>
                                <template
                                    v-if="slot.open.length && slot.booked.length"
                                >
                                    ·
                                </template>
                                <template v-if="slot.booked.length">
                                    已約 {{ slot.booked.length }}
                                </template>
                            </span>
                        </div>
                        <div class="sc__slot-people">
                            <button
                                v-for="p in viewMode === 'open' ? slot.open : []"
                                :key="`o-${p.core_member_id}`"
                                type="button"
                                class="sc__who sc__who--open"
                                :class="{ 'sc__who--static': !canManageConsultants }"
                                :title="
                                    canManageConsultants
                                        ? `${p.name}｜可約｜點擊開個別排班`
                                        : `${p.name}｜可約`
                                "
                                @click="goConsultantSchedule(p.core_member_id)"
                            >
                                <i
                                    class="sc__chip-dot"
                                    :style="{ background: p.color }"
                                    aria-hidden="true"
                                />
                                {{ p.name }}
                                <span class="sc__who-tag">可約</span>
                            </button>
                            <div
                                v-for="p in viewMode === 'booked' ? slot.booked : []"
                                :key="`b-${p.core_member_id}`"
                                class="sc__who sc__who--booked sc__who--row"
                            >
                                <button
                                    type="button"
                                    class="sc__who-main"
                                    :class="{ 'sc__who--static': !canManageConsultants }"
                                    @click="goConsultantSchedule(p.core_member_id)"
                                >
                                    <i
                                        class="sc__chip-dot"
                                        :style="{ background: p.color }"
                                        aria-hidden="true"
                                    />
                                    <span>
                                        {{ bookedCaption(p.name, p.student_name) }}
                                        <small
                                            v-if="modeLabel(p.mode) || p.status"
                                            class="sc__who-sub"
                                        >
                                            {{
                                                [modeLabel(p.mode), p.status ? bookingStatusLabel(p.status) : '']
                                                    .filter(Boolean)
                                                    .join(' · ')
                                            }}
                                        </small>
                                    </span>
                                </button>
                                <button
                                    v-if="canTransfer(p)"
                                    type="button"
                                    class="sc__transfer-btn"
                                    @click="openTransfer(p)"
                                >
                                    轉移
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </Drawer>

            <Modal
                v-model:open="transferOpen"
                title="轉移已約學員"
                ok-text="確認轉移"
                cancel-text="取消"
                :confirm-loading="transferSaving"
                @ok="submitTransfer"
            >
                <p v-if="transferBooking" class="sc__transfer-lead">
                    {{
                        bookedCaption(
                            displayName(transferBooking.consultant_core_member_id),
                            transferBooking.student_name
                        )
                    }}
                    <template v-if="modeLabel(transferBooking.mode)">
                        · {{ modeLabel(transferBooking.mode) }}
                    </template>
                    · {{ taipeiDay(transferBooking.starts_at).format('M月D日 HH:mm') }}
                </p>
                <Select
                    v-model:value="transferTarget"
                    :options="transferOptions"
                    show-search
                    allow-clear
                    placeholder="選擇接手的諮詢師"
                    class="sc__transfer-select"
                    :filter-option="
                        (input, option) =>
                            (option?.label ?? '')
                                .toLowerCase()
                                .includes(String(input).toLowerCase())
                    "
                />
                <p v-if="transferBooking && !transferOptions.length" class="sc__transfer-empty">
                    這個時段沒有可接手的諮詢師。
                </p>
            </Modal>
        </template>
    </div>
</template>

<style scoped>
.sc__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.sc__nav {
    display: flex;
    align-items: center;
    gap: 8px;
}

.sc__month {
    min-width: 7.5rem;
    text-align: center;
    font-weight: 700;
    color: #3f3a32;
    font-size: 16px;
}

.sc__filter {
    min-width: 180px;
}

.sc__meta {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.sc__switch {
    display: inline-flex;
    padding: 3px;
    border-radius: 999px;
    background: #f4f0e8;
    gap: 2px;
}

.sc__switch button {
    border: 0;
    background: transparent;
    border-radius: 999px;
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 650;
    color: #6b6458;
    cursor: pointer;
}

.sc__switch button.is-on {
    background: #3f3a32;
    color: #fff;
}

.sc__chip--student {
    align-items: flex-start;
    white-space: normal;
    border-radius: 8px;
    background: #fff7ed;
}

.sc__chip-booked {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
}

.sc__chip-mode {
    font-size: 11px;
    font-weight: 650;
    color: #9a3412;
}

.sc__who--row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    padding: 8px 8px 8px 10px;
    border-radius: 12px;
}

.sc__who-main {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    border: 0;
    background: transparent;
    padding: 0;
    color: #1e293b;
    cursor: pointer;
    text-align: left;
}

.sc__pending {
    margin-bottom: 16px;
    padding: 14px 16px;
    border: 1px solid #ebe4d6;
    border-radius: 16px;
    background: #fff;
}
.sc__pending-title {
    margin: 0 0 8px;
    font-weight: 700;
    color: #44403c;
}
.sc__pending-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    color: #1e293b;
    font-size: 14px;
}
.sc__pending-actions {
    display: flex;
    gap: 8px;
}
.sc__pending-wait {
    color: #78716c;
    font-size: 13px;
}
.sc__transfer-btn {
    flex: 0 0 auto;
    height: 28px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid #d6d3d1;
    background: #fff;
    color: #44403c;
    font-size: 12px;
    font-weight: 650;
    line-height: 1;
    cursor: pointer;
}

.sc__transfer-btn:hover {
    border-color: #a8a29e;
    color: #1c1917;
}

.sc__who-sub {
    display: block;
    color: #8a8378;
    font-size: 12px;
}

.sc__transfer-lead {
    margin-bottom: 12px;
    color: #3f3a32;
}

.sc__transfer-select {
    width: 100%;
}

.sc__transfer-empty {
    margin-top: 10px;
    color: #8a8378;
    font-size: 13px;
}

.sc__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-size: 13px;
    color: #3f3a32;
    font-weight: 600;
}

.sc__stat {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.sc__stat--muted {
    color: #64748b;
    font-weight: 500;
}

.sc__dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    display: inline-block;
}

.sc__dot--open {
    background: #16a34a;
}

.sc__dot--booked {
    background: #2563eb;
}

.sc__hint {
    margin: 0;
    font-size: 12px;
    color: #94a3b8;
}

.sc__loading {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 48px 16px;
    justify-content: center;
    color: #64748b;
}

.sc__weekdays {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    text-align: center;
}

.sc__weekdays .is-weekend {
    color: #94a3b8;
}

.sc__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 8px;
}

.sc__cell {
    min-height: 118px;
    padding: 8px;
    border: 1px solid #ebe4d6;
    border-radius: 14px;
    background: #fffcf7;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
}

.sc__cell:hover:not(:disabled) {
    border-color: #c4a574;
    box-shadow: 0 4px 14px rgba(63, 58, 50, 0.06);
    transform: translateY(-1px);
}

.sc__cell.is-empty {
    border-color: transparent;
    background: transparent;
    cursor: default;
    min-height: 0;
    padding: 0;
}

.sc__cell.is-blocked {
    background: #f8fafc;
    cursor: not-allowed;
    opacity: 0.72;
}

.sc__cell.is-today {
    border-color: #b45309;
    box-shadow: inset 0 0 0 1px rgba(180, 83, 9, 0.25);
}

.sc__cell.heat-1 {
    background: #fffaf3;
}

.sc__cell.heat-2 {
    background: #fff4e5;
}

.sc__cell.heat-3 {
    background: #ffecd4;
}

.sc__cell-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    margin-bottom: 8px;
}

.sc__daynum {
    font-weight: 800;
    color: #3f3a32;
    font-size: 13px;
}

.sc__pills {
    display: inline-flex;
    gap: 4px;
}

.sc__pill {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
    color: #fff;
}

.sc__pill--open {
    background: #16a34a;
}

.sc__pill--booked {
    background: #2563eb;
}

.sc__blocked-tag {
    font-size: 11px;
    color: #94a3b8;
}

.sc__chips {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.sc__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 100%;
    padding: 2px 6px 2px 4px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid #ebe4d6;
    font-size: 11px;
    font-weight: 600;
    color: #3f3a32;
    line-height: 1.3;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.sc__chip--more {
    color: #92400e;
    background: rgba(180, 83, 9, 0.08);
    border-color: transparent;
    justify-content: center;
}

.sc__chip-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    flex: 0 0 auto;
}

.sc__chip-dot--lg {
    width: 10px;
    height: 10px;
}

.sc__none {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #57534e;
}

.sc__slot + .sc__slot {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #ebe4d6;
}

.sc__slot-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}

.sc__slot-time {
    font-size: 14px;
    color: #1e293b;
}

.sc__slot-meta {
    font-size: 12px;
    color: #94a3b8;
    white-space: nowrap;
}

.sc__slot-people {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.sc__who {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 8px 5px 6px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    cursor: pointer;
    transition: filter 0.15s ease;
}

.sc__who--static {
    cursor: default;
}

.sc__who:hover:not(.sc__who--static) {
    filter: brightness(0.97);
}

.sc__who--open {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
}

.sc__who--booked {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1e40af;
}

.sc__who-tag {
    font-size: 11px;
    font-weight: 500;
    opacity: 0.75;
}

@media (max-width: 960px) {
    .sc__cell {
        min-height: 96px;
        padding: 6px;
    }

    .sc__chip {
        font-size: 10px;
    }
}
</style>
